/**
 * Lightweight hash-based router.
 *
 * Why hash and not History API:
 *  - Works under file:// (Capacitor/TG/LINE bundle file://)
 *  - No server rewrites needed for any deploy target
 *  - Bundle weight ~500 bytes
 *
 * Screens register a factory; the router mounts/unmounts on nav.
 * Each screen receives a host element and returns either an HTMLElement
 * (rendered into the host) or a promise for one.
 */

export interface RouteContext {
  /** Path matched (e.g. "/play/2026-06-15"). */
  path: string;
  /** Path parameters extracted by `:param` segments. */
  params: Record<string, string>;
  /** Query string params. */
  query: URLSearchParams;
  /** Navigate programmatically (preserves history). */
  navigate: (to: string, opts?: { replace?: boolean }) => void;
}

export type Screen =
  | HTMLElement
  | DocumentFragment
  | { node: HTMLElement | DocumentFragment; cleanup?: () => void };

export type ScreenFactory = (ctx: RouteContext) => Screen | Promise<Screen>;

interface Route {
  pattern: string;
  segments: Array<{ literal: string } | { param: string }>;
  factory: ScreenFactory;
}

const routes: Route[] = [];
let host: HTMLElement | null = null;
let activeCleanup: (() => void) | null = null;
let mountVersion = 0;

export function defineRoute(pattern: string, factory: ScreenFactory): void {
  const segments = pattern
    .split('/')
    .filter((s) => s.length > 0)
    .map((s) => (s.startsWith(':') ? { param: s.slice(1) } : { literal: s }));
  routes.push({ pattern, segments, factory });
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  const target = to.startsWith('#') ? to : `#${to.startsWith('/') ? to : '/' + to}`;
  if (location.hash === target) {
    void resolve(); // re-trigger if same hash
    return;
  }
  if (opts.replace) {
    history.replaceState(null, '', target);
  } else {
    location.hash = target;
    return; // hashchange will fire
  }
  void resolve();
}

export function currentPath(): string {
  return (location.hash || '#/').slice(1) || '/';
}

export function startRouter(mountEl: HTMLElement): void {
  host = mountEl;
  window.addEventListener('hashchange', () => void resolve());
  void resolve();
}

async function resolve(): Promise<void> {
  if (!host) return;
  const fullHash = location.hash.slice(1) || '/';
  const [rawPath, rawQuery = ''] = fullHash.split('?');
  const path = (rawPath ?? '/').replace(/\/+$/, '') || '/';
  const query = new URLSearchParams(rawQuery);

  const segs = path.split('/').filter(Boolean);

  for (const route of routes) {
    if (route.segments.length !== segs.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < segs.length; i++) {
      const r = route.segments[i]!;
      const s = segs[i]!;
      if ('literal' in r) {
        if (r.literal !== s) {
          ok = false;
          break;
        }
      } else {
        params[r.param] = decodeURIComponent(s);
      }
    }
    if (!ok) continue;
    await mount(route.factory, { path, params, query, navigate });
    return;
  }
  // fallback: try root
  if (path !== '/') {
    navigate('/', { replace: true });
    return;
  }
}

async function mount(factory: ScreenFactory, ctx: RouteContext): Promise<void> {
  if (!host) return;
  const myVersion = ++mountVersion;
  if (activeCleanup) {
    try {
      activeCleanup();
    } catch {
      /* ignore */
    }
    activeCleanup = null;
  }
  try {
    const result = await factory(ctx);
    // Abort if a newer navigation started while we were loading.
    if (myVersion !== mountVersion || !host) return;
    host.innerHTML = '';
    if (result instanceof Node) {
      host.appendChild(result);
    } else {
      host.appendChild(result.node);
      if (result.cleanup) activeCleanup = result.cleanup;
    }
    host.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: 'auto' });
  } catch (err) {
    if (myVersion !== mountVersion || !host) return;
    console.error('mount failed', err);
    host.innerHTML = '<p style="padding:24px;text-align:center;">Something went wrong.</p>';
  }
}
